import { useLayoutEffect, useState, useEffect, RefObject } from 'react';

export enum DropdownPosition {
  Top = 'top',
  Bottom = 'bottom',
}

/**
 * Shared adaptive dropdown positioning + visibility scroll logic.
 * Flips between bottom/top if available space on the requested side
 * is below threshold and opposite side has more room.
 *
 * @param containerRef Reference to the wrapper element
 * @param show Whether the dropdown is currently visible
 * @param requestedPosition The preferred position to open in
 * @param dropdownMaxHeightPx The estimated max-height of the dropdown component. Used as the spatial bound to prevent bleeding off the bottom of the screen.
 */
export function useAdaptiveDropdownPosition(
  containerRef: RefObject<HTMLElement>,
  show: boolean,
  requestedPosition: DropdownPosition,
  dropdownMaxHeightPx = 200, // Safe default max height
): DropdownPosition {
  const [position, setPosition] = useState(requestedPosition);

  useLayoutEffect(() => {
    if (!show) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;

    if (requestedPosition === DropdownPosition.Bottom) {
      if (spaceBelow < dropdownMaxHeightPx && spaceAbove > spaceBelow) {
        setPosition(DropdownPosition.Top);
      } else {
        setPosition(DropdownPosition.Bottom);
      }
    } else if (spaceAbove < dropdownMaxHeightPx && spaceBelow > spaceAbove) {
      setPosition(DropdownPosition.Bottom);
    } else {
      setPosition(DropdownPosition.Top);
    }

    if (rect.bottom > vh || rect.top < 0) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [show, requestedPosition, dropdownMaxHeightPx, containerRef]);

  // Reset when hidden and prop changes
  useEffect(() => {
    if (!show) setPosition(requestedPosition);
  }, [requestedPosition, show]);

  return position;
}
